import { useState } from 'react'
import { ArrowDown, ArrowUp, GitBranch, Plus, Trash2, UserCheck, UserCog, UserRound, Users, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import ConditionEditor, { type ConditionField } from '../../components/ConditionEditor'
import type { AssigneeSpec } from '../../types'
import { PersonPicker } from './PersonPicker'
import {
    MAX_RULE_DEPTH, addCase, assigneeKind, assigneeLabel, moveCase, newConditional, removeAt, removeCase,
    replaceAt, setElse, updateCase, type LabelCtx,
} from './model'

export interface AssigneeCtx extends LabelCtx {
    conditionFields: ConditionField[]
}

type Conditional = Extract<AssigneeSpec, { rule: 'conditional' }>
type Adder = null | 'user' | 'group' | 'field'

const kindIcon = {
    user: UserRound, group: Users, submitter_manager: UserCog, field: UserCheck, conditional: GitBranch,
} as const

function Chip({ spec, ctx, onRemove }: { spec: AssigneeSpec; ctx: LabelCtx; onRemove: () => void }) {
    const kind = assigneeKind(spec)
    const Icon = kindIcon[kind]
    const label = assigneeLabel(spec, ctx)
    return (
        <Badge variant="secondary" className="h-7 gap-1.5 pr-1">
            <Icon data-icon="inline-start" />
            <span className="max-w-56 truncate">{label}</span>
            <button type="button" onClick={onRemove} aria-label={`Remove ${label}`}
                className="rounded-full p-0.5 hover:bg-foreground/10 focus-visible:outline-2 focus-visible:outline-ring">
                <X className="size-3" />
            </button>
        </Badge>
    )
}

function ConditionalBlock({ spec, ctx, depth, onChange, onRemove }: {
    spec: Conditional
    ctx: AssigneeCtx
    depth: number
    onChange: (s: Conditional) => void
    onRemove: () => void
}) {
    return (
        <div className="flex flex-col gap-3 rounded-lg border border-dashed bg-muted/30 p-3">
            <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-sm font-medium"><GitBranch className="size-4" /> Conditional assignees</span>
                <Button type="button" variant="ghost" size="sm" onClick={onRemove} aria-label="Remove conditional assignee">
                    <Trash2 data-icon="inline-start" /> Remove
                </Button>
            </div>
            {spec.cases.map((c, i) => (
                <div key={i} className="flex flex-col gap-2 rounded-md border bg-background p-3">
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold uppercase text-muted-foreground">{i === 0 ? 'If' : 'Else if'}</span>
                        <div className="flex gap-1">
                            <Button type="button" variant="ghost" size="icon-sm" disabled={i === 0} aria-label={`Move case ${i + 1} up`}
                                onClick={() => onChange(moveCase(spec, i, i - 1))}><ArrowUp /></Button>
                            <Button type="button" variant="ghost" size="icon-sm" disabled={i === spec.cases.length - 1} aria-label={`Move case ${i + 1} down`}
                                onClick={() => onChange(moveCase(spec, i, i + 1))}><ArrowDown /></Button>
                            <Button type="button" variant="ghost" size="icon-sm" aria-label={`Delete case ${i + 1}`}
                                onClick={() => onChange(removeCase(spec, i))}><Trash2 /></Button>
                        </div>
                    </div>
                    <ConditionEditor value={c.when} fields={ctx.conditionFields} emptyText="Always"
                        onChange={(when) => onChange(updateCase(spec, i, { when: when ?? { all: [] } }))} />
                    <span className="text-xs font-semibold uppercase text-muted-foreground">Then assign</span>
                    <AssigneeEditor value={c.assignees} ctx={ctx} depth={depth + 1}
                        onChange={(assignees) => onChange(updateCase(spec, i, { assignees }))} />
                </div>
            ))}
            <Button type="button" variant="outline" size="sm" className="self-start" onClick={() => onChange(addCase(spec))}>
                <Plus data-icon="inline-start" /> Add case
            </Button>
            <div className="flex flex-col gap-2 rounded-md border bg-background p-3">
                <span className="text-xs font-semibold uppercase text-muted-foreground">Otherwise assign</span>
                <AssigneeEditor value={spec.else ?? []} ctx={ctx} depth={depth + 1} onChange={(list) => onChange(setElse(spec, list))} />
            </div>
        </div>
    )
}

interface Props {
    value: AssigneeSpec[]
    onChange: (next: AssigneeSpec[]) => void
    ctx: AssigneeCtx
    depth?: number
}

/** Mixed list of assignee chips plus an "Add" menu. Recursive for conditional rules. */
export function AssigneeEditor({ value, onChange, ctx, depth = 0 }: Props) {
    const [adder, setAdder] = useState<Adder>(null)
    const canNest = depth < MAX_RULE_DEPTH - 1
    const add = (s: AssigneeSpec) => { onChange([...value, s]); setAdder(null) }

    const chips = value.map((s, i) => ({ s, i })).filter(({ s }) => assigneeKind(s) !== 'conditional')
    const blocks = value.map((s, i) => ({ s, i })).filter(({ s }) => assigneeKind(s) === 'conditional')
    const pickedUsers = value.flatMap((s) => (s.kind === 'user' ? [s.userId] : []))

    return (
        <div className="flex flex-col gap-2">
            {value.length === 0 && <p className="text-xs text-muted-foreground">No assignees yet.</p>}
            {chips.length > 0 && (
                <ul className="flex flex-wrap gap-1.5" aria-label="Assignees">
                    {chips.map(({ s, i }) => (
                        <li key={i}><Chip spec={s} ctx={ctx} onRemove={() => onChange(removeAt(value, i))} /></li>
                    ))}
                </ul>
            )}
            {chips.map(({ s, i }) => s.kind === 'rule' && s.rule === 'submitter_manager' && (
                <div key={`fb${i}`} className="flex flex-col gap-2 rounded-md border bg-background p-3">
                    <span className="text-xs font-semibold uppercase text-muted-foreground">If no manager is on record, assign</span>
                    <AssigneeEditor value={s.else ?? []} ctx={ctx} depth={depth + 1}
                        onChange={(list) => onChange(replaceAt(value, i, { ...s, else: list.length ? list : undefined }))} />
                </div>
            ))}
            {blocks.map(({ s, i }) => (
                <ConditionalBlock key={i} spec={s as Conditional} ctx={ctx} depth={depth}
                    onChange={(n) => onChange(replaceAt(value, i, n))} onRemove={() => onChange(removeAt(value, i))} />
            ))}

            <div className="flex flex-wrap items-center gap-2">
                {adder === null && (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button type="button" variant="outline" size="sm"><Plus data-icon="inline-start" /> Add assignee</Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                            <DropdownMenuGroup>
                                <DropdownMenuItem onSelect={() => setAdder('user')}><UserRound /> Person</DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => setAdder('group')}><Users /> Group</DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => add({ kind: 'rule', rule: 'submitter_manager' })}><UserCog /> Submitter's manager</DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => setAdder('field')}><UserCheck /> Person named on the form</DropdownMenuItem>
                                {canNest && <DropdownMenuItem onSelect={() => add(newConditional())}><GitBranch /> Conditional</DropdownMenuItem>}
                            </DropdownMenuGroup>
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
                {adder === 'user' && (
                    <PersonPicker mode="select" placeholder="Search for a person…" exclude={pickedUsers}
                        onSelect={(p) => add({ kind: 'user', userId: p.id, name: p.name })} />
                )}
                {adder === 'group' && (
                    <Select onValueChange={(groupId) => add({ kind: 'group', groupId })}>
                        <SelectTrigger className="w-60" aria-label="Choose a group"><SelectValue placeholder="Choose a group…" /></SelectTrigger>
                        <SelectContent><SelectGroup>
                            {ctx.groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                        </SelectGroup></SelectContent>
                    </Select>
                )}
                {adder === 'field' && (
                    ctx.userRefFields.length === 0 ? (
                        <span className="text-xs text-muted-foreground">No published form has a person field.</span>
                    ) : (
                        <Select onValueChange={(field) => add({ kind: 'rule', rule: 'field', field })}>
                            <SelectTrigger className="w-72" aria-label="Choose a form field"><SelectValue placeholder="Choose a form field…" /></SelectTrigger>
                            <SelectContent><SelectGroup>
                                {ctx.userRefFields.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                            </SelectGroup></SelectContent>
                        </Select>
                    )
                )}
                {adder !== null && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => setAdder(null)}>Cancel</Button>
                )}
            </div>
        </div>
    )
}
